import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import constants from 'scoreboard/components/constants'
import conductor from 'scoreboard/components/conductor'
import config from 'scoreboard/components/config'
import layoutManager from 'scoreboard/components/layout-manager'
import spectator from 'scoreboard/components/spectator'
import unwrapModel from 'scoreboard/components/unwrap-model'

let debug = false

/** Exports will increase the chart by this factor to get a smaller font on the chart,
 *  and then apply a css transform:scale to fit in the allotted space.
 */
var CHART_SCALE_FACTOR = 1.15

/**
 * Used in *-overview-export.hbs
 */
const CHART_CLASS= 'export-historical'

function dispatchReadyEvent(customEventArg) {
	if (debug) {
		console.log('dispatching ready event:', customEventArg)
	}
	try {
		document.body.dispatchEvent(new CustomEvent('ready', customEventArg))
	} catch (e) {
		console.log('error calling readyDispatch', e)
	}
}

/**
 * Ensures that the view is ready to be captured during the export process
 * @param view
 * @param {object} options
 * @param {boolean} [options.ignoreReadyAcknowledged=false] allow tests to turn
 * off this event emitter
 * @constructor
 */
function ExportPreparer(view, options) {
	this.view = view
	/** Options provided by the view (i.e. autoscale) */
	this.options = options || {}
	/**
	 * Options emitted for the electron-pdf module in the
	 * scoreboard-export server to consume (i.e. landscape)
	 * */
	this.exportOptions = {}
}

_.extend(ExportPreparer.prototype, {

	/**
	 * Invoked after the scoreboard view is loaded.
	 *
	 * Allows additional actions to be take to prepare the view for export
	 * and dispatch a 'ready' event from document.body
	 *
	 * scoreboard-export listens for this event to be fired before capturing
	 * the page.
	 *
	 * A CustomEvent is emitted with details that indicate if the orientation
	 * should be set to landscape or not.
	 */
	prepare: function() {
		if (!this.view) {
			console.error('no view provided, cannot prepare for export')
			return
		}
		if (layoutManager.export) {
			console.log('preparing view for export...')
			if (this.view._readyBroadcasted) {
				// While troubleshooting #26885 it was found that despite calling
				// this._display.once('ready', _broadcastViewReady)
				// this function could be invoked multiple (2) times.
				// This can have adverse affects on export layout calculations, so
				// this is here to ensure it's only ever executed once.
				return
			}
			this.view._readyBroadcasted = true
			if (layoutManager.exportType === 'xlsx' || layoutManager.exportType === 'csv') {
				// Triggers a download, which the export server listens for.
				_.invoke(this.view, 'exportTable')
			} else {
				// When the export server is running on a Windows OS charts may not
				// fully render without using nextTick here (#11953).  The issue
				// is non-reproducible on OS X
				process.nextTick(this._handleReadyEvent.bind(this,this.options))
			}
		}
	},

	_handleReadyEvent: function(options) {
		var opts = options || {}

		// Allows specialized styles to be applied based on media type (fixes #10204)
		$('body').toggleClass(`${layoutManager.exportType}-export`, true)

		if (layoutManager.orientation === constants.ORIENTATION_LANDSCAPE) {
			this.exportOptions.landscape = true
		}

		// We have had a number of Chrome rendering issues with respect to
		// dashboards in PowerPoint exports.  So, because they are a single page
		// and we convert them to PNGs anyway, we decided to just directly capture
		// them as PNGs, which do not exhibit the same problems as printToPDF (#25172)
		const spectatedUrl = _.get(unwrapModel(spectator.getCurrent()), 'url')
		if (layoutManager.exportType === 'pptx' &&
			conductor.section(spectatedUrl) === 'dashboards') {
			this.exportOptions.pngDirect = true
			// Fixes #26212
			// 1600x1200; See MicronDimension.java for conversion factor
			this.exportOptions.width = 423333
			this.exportOptions.height = 317500
		}

		//If a view (i.e. dashboards) scales itself, don't scale it again!
		if (opts.autoScale) {
			// layouts.scss sets overflow to visible (dashboard-shared).
			// This is desireable everywhere except for dashboards
			var body = $('body:has(.display-layout-dashboards)')
			// http://stackoverflow.com/a/11963008/5656782
			var style = body.prop('style')
			style && style.setProperty('overflow', 'hidden', 'important')

			this._emitCustomEvent()
		} else {
			this._scaleExport(this._emitCustomEvent.bind(this))
			/*
			 This used to be in modes.scss, but it can't be set there because it
			 will break dashboard auto-scaling, but it needs to be set here or
			 pages like scorecard metrics will not flow beyond the 1st page.
			 */
			this.view.$el.parent('.display-pane').css('height','auto')
		}
	},

	/**
	 * The export process will listen for this event before capturing the page
	 * @private
	 */
	_emitCustomEvent: function () {
		var customEventSupported = typeof window['CustomEvent'] === 'function'
		if (customEventSupported) {
			// Electron 8+ doesn't support passing functions, so we pick only the attributes
			// the export server needs here
			// https://github.com/electron/electron/blob/8de06f0c571bc24e4230063e3ef0428390df773e/docs/breaking-changes.md#L201
			const {landscape, pngDirect, width, height} = this.exportOptions
			const detail = {landscape, pngDirect, width, height}
			const customEventArg = {detail} // detail is a mandatory property in CustomEvent
			$('body').toggleClass('export-ready')
			Backbone.trigger('export:ready') // #42263; charts workaround
			// This dispatch loop makes tests go haywire, allow it to be turned off
			if (!this.options.ignoreReadyAcknowledged) {
				this._listenForReadyAcknowledgment(customEventArg)
			} else {
				dispatchReadyEvent(customEventArg)
			}
		}
	},

	/**
	 * electron-pdf communicates using an inter process communication (IPC) channel
	 * so that the export job can continue when the browser window receives the 'ready'
	 * event that we are emitting.
	 *
	 * However, if the page is rendered very quickly, this IPC message may not be
	 * received.  For this reason electron-pdf emits an 'ready-acknowledged' event that
	 * we listen for.  This method continues to emit the 'ready' event until we receive
	 * an acknowledgment.
	 *
	 * @param {Object} customEventArg an object to emit as part of the CustomEvent
	 * @private
	 */
	_listenForReadyAcknowledgment: function (customEventArg) {
		var retryInterval = 25 // millis
		var maxWait = 5000 // millis
		var count = 0

		var interval = setInterval(function () {
			dispatchReadyEvent(customEventArg)
			// Ideally this should only run during exports and always dispatch successfully,
			// but just in case cancel it after 5 seconds
			if (count++ * retryInterval > maxWait) {
				clearInterval(interval)
				console.debug('Giving up waiting for export ready acknowledgement')
			}
		}, retryInterval)
		document.body.addEventListener('ready-acknowledged', () => {
			clearInterval(interval)
		})
	},

	/**
	 * Exports may have scrollable content that will not fit on the page.
	 * If they do, this method will scale it down in landscape orientation.
	 *
	 * @param {function} viewReadyFn to be invoked when the client is ready
	 * @private
	 */
	_scaleExport: function(viewReadyFn) {

		// Only restructure DOM for the following tables:
		// Don't add new ones without testing the output, and make sure only
		// one table will be targeted by the CSS Selector
		const dataTableSelectors = [
			'.scorecards-metrics .panel-with-table',
			'.reports-overview .panel-with-table']
		const table = $(dataTableSelectors.join(','))

		/**
		 * Move the table outside of all the table hacks which prevents page breaks
		 * from working properly (#26504)
		 * FIXME:23909 When IE table hacks are removed this should be removed too
		 */
		const undoTableHack = () => {
			// Only do this for reports and the scorecard metrics tab
			const body = $('.display-pane-body')
			if (table.length === 1 &&
				// IMPORTANT: Can't detach the table if there is nowhere to add it back
				body.length === 1) {
				body.replaceWith(table.detach())
			}
		}

		// A view can have zero or more scalable elements
		var scalableEls = this.view.$('.scalable')
		if (scalableEls.length === 0) {
			undoTableHack()
			viewReadyFn()
		} else {
			const singleTable = scalableEls.length === 1 && table.length === 1
			const dims = this._readDimensions(scalableEls.get(0))
			if (singleTable && !dims.requiresScaling()) {
				this._orientPageForContainer(dims, table, () => {
					undoTableHack()
					viewReadyFn()
				})
			} else {
				_.each(scalableEls, container => {
					const dims = this._readDimensions(container)
					this._orientPageForContainer(dims, container, viewReadyFn)
				})
			}
		}
	},

	_orientPageForContainer: function(dimensions, container, readyFn) {
		const {portraitMode, pageWidth, pageHeight} = dimensions

		if (container) {
			var containerObj = $(container)
			// If there are multiple scalable containers,
			// don't ever flip back when one fits and the other doesn't
			this.exportOptions.landscape = this.exportOptions.landscape || dimensions.isLandscape()
			this.exportOptions.portraitMode = portraitMode
			this.exportOptions.dimensions = dimensions

			const transform = (height, timeout) => {
				clearTimeout(timeout)
				if (dimensions.requiresScaling()) {
					const afterRotateDims = this._readDimensions(container)
					this._applyCssScaling(afterRotateDims, container, containerObj)
				}

				// #50087 always resize the chart even if scaling isn't required;
				// any change in dimensions (such as rotating from portrait to
				// landscape) will require the chart to be resized. And resizing
				// unnecessarily doesn't seem to cause any problems.
				this._resizeChart(containerObj, height, readyFn)
			}

			if (dimensions.isLandscape()) { // Rotate, wait for resize events, then scale
				// This is for manual testing only, Electron/Chromium does the resizeTo, Chrome does not
				const timeout = setTimeout(transform.bind(this, pageWidth), 1000)
				// The transformations need to happen after the window resize
				// so everything reflows and margins, etc. are applied
				$(window).one('resize', transform.bind(this, pageWidth, timeout))
				window.resizeTo(pageHeight, pageWidth)
			} else if (dimensions.requiresScaling()) { // No reorienting, just scale
				transform(pageHeight)
			} else { // Dimensions fine as is, just fit the chart
				this._resizeChart(containerObj, pageHeight, readyFn)
			}
		}
	},

	_applyCssScaling: function (dims, c, cObj) {

		// Adding 1 pixel alleviates the element from extending over the panel border
		var ratio = dims.parentWidth / (dims.width + 1)

		// individual interactions with cObj are to workaround test issue
		let cellContainer = cObj.parent()
		cellContainer.height(_.ceil(dims.height * ratio) + 'px')
		cObj.css('transformOrigin', 'top left')
		cObj.css('transform', 'scale(' + ratio + ')')
		// This is needed to prevent a transform-origin bug when a scaled table will randomly be aligned
		// to the bottom of the export. Issue #26635
		// It needs to go after the height and scaling values have been added or else the height value will be 0.
		cellContainer.parent().css('display', 'inline-block')
	},

	_readDimensions: function (element) {
		// This is a custom size we have to specify in microns when calling the export server
		const POWERPOINT = config.exportDimensions['four-three'] // this is in Pixels
		// This is a standard size built-in to electron that we must specify as 'Letter'
		// So we have no enum representation for it and just hard-code the dimensions here
		const LETTER = {width: 816, height: 1056}

		const pptx43Mode = layoutManager.orientation === constants.ORIENTATION_43
		const mediaType = pptx43Mode ? POWERPOINT : LETTER

		const pageWidth = mediaType.width
		const pageHeight = mediaType.height
		const MAX_UNSCALED_WIDTH = Math.max(pageWidth, pageHeight)
		const parentWidth = $(element).parent().width()

		const width = element.scrollWidth
		const height = element.scrollHeight
		// Flip orientation if we are in portrait mode
		const portraitMode = layoutManager.orientation === constants.ORIENTATION_PORTRAIT
		const canFlip = layoutManager.exportType !== 'docx'

		return {
			portraitMode,
			pageWidth,
			pageHeight,
			parentWidth,
			scrollWidth: this._getScrollWidth($('body').prop('scrollWidth'), width),
			width,
			height,
			isLandscape: function() {
				return portraitMode && canFlip && this.scrollWidth > this.pageWidth
			},
			/**
			 * If the container will require scaling to fit on the page, either
			 * in portrait mode or landscape (if allowed)
			 */
			requiresScaling: function() {
				const upperBound = canFlip ? MAX_UNSCALED_WIDTH : this.parentWidth
				return this.width > upperBound
			}
		}
	},


	/**
	 * #12308 - body.scrollWidth is not reported correctly on Windows, so we fall
	 * back to the width of the scalable container
	 */
	_getScrollWidth: function (bodyScrollWidth, scalableContainerWidth) {
		return Math.max(bodyScrollWidth, scalableContainerWidth)
	},

	/**
	 * See if this scalable element has an associated chart
	 * Charts should scale to 50% of the page if the table allows
	 *
	 * This function is responsible to find the relevent DOM elements and
	 * delegate to _transformChart
	 *
	 * @param {jQuery} scalableElement
	 * 	Can be a table or a chart with a .scalable class defined
	 * 	If there is no .associated-chart or .export-historical class on the
	 * 	scalable element, then no action will be taken.
	 *
	 * @param {number} pageHeight
	 * 	in pixels (i.e. 816 or 1056)
	 *
	 * @param {function} callback
	 * 	Will be passed the table object after the associated chart has been resized
	 */
	_resizeChart: function(scalableElement, pageHeight, callback) {
		let chart, table, tableParent
		let chartName = scalableElement.data('associated-chart')
		if (chartName) {
			// Calculate chart height from the entire table panel with header/margins/etc.
			// But if no selector is set just use the table height
			table = scalableElement
			const tableParentSelector = scalableElement.data('top-table-element')
			tableParent = tableParentSelector ? scalableElement.parents(tableParentSelector) : scalableElement
			chart = this.view.$(chartName)
		} else if (scalableElement.hasClass(CHART_CLASS)) {
			chart = scalableElement
		}

		var context = {table, chart: chartName}
		if (chart) {
			if (!chart.hasClass('export-ready')) { // Must not transform chart more than once (#16463)
				const dim = this._transformChart(pageHeight, chart, tableParent || chart)

				// 'export-prepared' means that any preparation needed before SB3 emits 'ready'
				// is complete.  'ready' is reserved for notifying the Electron window that it
				// can send the IPC message to the main process and capture the page for export.
				this.view.once('export-prepared', callback.bind(undefined, context))

				// Let the view resize anything based on these new dimensions (i.e. charts)
				this.view.trigger('export-resize', dim)
			}
		} else {
			callback(context)
		}
	},

	/**
	 * Resizes and scales a chart based on page size and an associated table.
	 *
	 * @param {number} pageHeight in pixels (at 96 px/inch)
	 * @param {jQuery} chartObj The chart that we are transforming
	 * @param {jQuery} [tableParent] The element that contains the table
	 *
	 * @private
	 */
	_transformChart: function (pageHeight, chartObj, tableParent) {

		// PDF uses 72 DPI, HTML and the export views (css) uses 96.  But if 96
		// is used then the table actually exceeds half of the page.  If 72 is used
		// all kinds of bad things happen in SB3.  This is the best of both worlds for now
		var pageHeightFor72DPI = pageHeight * (72/96)
		var MIN_CHART_HEIGHT = 210

		const parentContainer = chartObj.parents(`.${CHART_CLASS}`)
		var historicalSectionHeight = parentContainer.height()
		var chartMargins = _.isFinite(historicalSectionHeight) ?
			historicalSectionHeight - chartObj.height() : 0
		// We want to show the chart on up to half the page if we have the space
		var maxChartHeight = (pageHeightFor72DPI / 2) - chartMargins

		const tableHeight = tableParent ? tableParent.height() : 0
		const availableHeight = pageHeightFor72DPI - tableHeight
		const heightAvailableAboveTable = Math.max(MIN_CHART_HEIGHT, availableHeight)

		// Makes the chart bigger so we can scale it back down and have smaller
		// fonts and more axis labels
		const newHeight = Math.min(heightAvailableAboveTable, maxChartHeight)
		const chartAspectRatio = chartObj.width() / newHeight
		const scaledHeight = newHeight * CHART_SCALE_FACTOR
		const scaledWidth = scaledHeight * chartAspectRatio
		chartObj.parent().height(newHeight)
		chartObj.css('height', scaledHeight)
		chartObj.css('max-height', maxChartHeight * CHART_SCALE_FACTOR)
		chartObj.width(scaledWidth)

		// Scales the chart down to fit correctly on the page
		chartObj.css('transformOrigin', 'top left')
		chartObj.css('transform', 'scale(' + (1 / CHART_SCALE_FACTOR) + ')')

		chartObj.addClass('export-ready')

		// console.log(
		// 	'chart margins:', chartMargins,
		// 	'heightAvailableAboveTable:', heightAvailableAboveTable,
		// 	'maxChartHeight:', maxChartHeight,
		// 	'newHeight:', newHeight)

		// Make the dimensions available to the event listener in historical.js
		return {
			height: scaledHeight,
			width: scaledWidth
		}
	}
})


export default ExportPreparer

ExportPreparer.__filename__ = 'components/export/export-preparer.js'
